// umlaut-check: skip — technische Schlüssel der Bestandskennzahlen (JSON-Schnittstelle).
import {nextNewRowKey, toEditorRows} from './rows';
import {emptyPending, isDirty, origOf} from './changes';
import {applyDiscountGroup, applyDiscounts} from './discount';
import {articleStammOf, isExcluded} from './availability';
import {WIZ_LAST, emptyKommWiz} from './wizard';
import {qtyNum} from './format';

// Zustand der Editor-Seite in einem Reducer (Beleg, Positionen, Bestand,
// Ansicht, vorgemerkte Kopf-Änderungen, Belegversion, Speicherlauf). Kein
// zusätzliches Zustands-Paket — so steht der gesamte Ablauf an einer Stelle
// und die Stufen 2–4 hängen sich an dieselben Aktionen an.

/** Leerer Bestandsblock. `loaded` ersetzt das `STOCK === null` des Originals. */
export const emptyStock = {
    loaded: false,
    free: {},
    freeByWh: {},
    // Kennzahlen je Artikel und Lager (einheitliche Bestandsregel, Auftrag #315):
    // lagerbestand, reserviert_fremd, reserviert_eigen, verfuegbar, offen_nicht_reserviert.
    avail: {},
    whStock: {},
    warehouses: [],
    groups: null,
    rules: [],
    staffelChannels: [],
    purchase: {},
    production: {},
    masterbox: {},
    masterboxRule: null,
    masterboxDiscount: {},
    dims: {},
    // VE-Regel (Auftrag #315): Größe der Verkaufseinheit je Artikel und die
    // Einstellungen (aktiv, ausgenommene Vertriebswege) vom Server.
    ve: {},
    veRegel: {aktiv: false, ausgenommen: []},
    names: {},
    voc: {},
    stammArt: {},
    shippingArticles: [],
    pallets: null,
    fracht: null,
    shipTo: null,
    lahrStockTrusted: true,
};

/** Ansichtszustand der Positionstabelle (Filter und Sortierung). */
export const emptyView = {
    availFilter: [],
    mbFilter: false,
    posSearch: '',
    artSearch: '',
    nameSearch: '',
    qtyMin: '',
    qtyMax: '',
    discMin: '',
    discMax: '',
    groupFilter: 'all',
    stammFilter: 'all',
    sort: {key: null, dir: 1},
    // Original `DISC_MANUAL`: „✎ von Hand setzen" klappt die Tabelle im
    // Rabatt-Schritt auf und hält den Schritt sichtbar (wizSkip 7).
    discManual: false,
};

/**
 * Zustand des geführten Ablaufs (Original `WIZ_STEP`, `PROGRESS`,
 * `KOMM_STATE`, `PROD_PLAN`, `ORDER_SOURCE`, `SNV_VERSAND`, `SHIP_DOCS`,
 * `SNV_INFO`, `ARRIVED`, `KOMM_WIZ`, `CHECKED_STOCK`). Anders als im Original
 * liegen die Marker und die Prüf-Haken serverseitig in `order_editor_states`
 * und sind damit für jeden Bearbeiter sichtbar (ABGLEICH G28, K32, K33).
 */
export const emptyWizard = {
    step: 1,
    // Fortschritt aus den Dokumenten am Beleg: {kommDoc, abDoc, docs}
    progress: null,
    // {docs, komm, kommDocs, printers, osName, preferred}
    kommState: null,
    kommLoading: false,
    prodPlan: null,
    prodPlanLoading: false,
    // „Wohin geht die Schneider-Ware?" — steuert nur den Liefer-Kommentar.
    orderSource: 'split',
    // Pflichtfeld am Schneider-Lieferschein: 'paket' | 'palette' | null
    snvVersand: null,
    shipDocs: {id: null, docs: null, loading: false},
    snvInfo: {id: null, data: null, loading: false},
    arrived: {id: null, data: null, loading: false},
    kommWiz: emptyKommWiz,
    // Prüf-Haken aus `order_editor_states.checked_item_ids`.
    checked: [],
    savedChecked: [],
    // Versandkosten-Schätzung aus Schritt 2 (Ticket #119), serverseitig gemerkt.
    shipEstimate: null,
};

/** Laufender bzw. letzter Lieferlauf eines Schritts (Original `#deliverStatus`/`#deliverResult`). */
export const emptyDeliver = {
    running: false,
    mode: null,
    status: null,
    result: null,
    docs: null,
};

/** Speicherlauf: Fortschritt und Statuskasten der Speicherleiste. */
export const emptySave = {
    running: false,
    jobId: null,
    phase: '',
    pct: 0,
    restSec: null,
    // {tone: 'ok'|'err'|'warn'|'work', text, weclappUrl}
    status: null,
};

export const initialState = {
    mode: 'order',
    order: null,
    rows: [],
    stock: emptyStock,
    view: emptyView,
    meta: {version: null},
    pending: emptyPending,
    // Gespeicherter Arbeitsstand aus `order_editor_states` (Positionen ohne
    // Lieferung). Er ist Ausgangsstand, keine offene Änderung.
    savedOverrides: {},
    save: emptySave,
    stale: false,
    newDocLinks: [],
    loading: true,
    loadError: null,
    stockLoading: false,
    vatResults: {},
    wizard: emptyWizard,
    deliver: emptyDeliver,
};

/** Eine Position an einer Stelle der Arbeitsliste ersetzen. */
function patchRow(rows, index, patch) {
    if (index < 0 || index >= rows.length) {
        return rows;
    }
    const next = rows.slice();
    next[index] = {...next[index], ...patch};
    return next;
}

/**
 * Gespeicherte Wahl „Keine Lieferung" als Ausgangsstand auf die Arbeitsliste
 * legen. Zeilen mit offener Änderung bleiben unangetastet — der Bearbeiter
 * soll seine Eingabe nicht durch einen nachlaufenden Abruf verlieren.
 */
function applyOverrides(rows, overrides) {
    const keys = Object.keys(overrides || {});
    if (!keys.length) {
        return rows;
    }
    return rows.map((r) => {
        const v = r.id != null ? overrides[String(r.id)] : undefined;
        if (!v || isDirty(r)) {
            return r;
        }
        const o = origOf(r) || {};
        return {
            ...r,
            _stammOverride: v,
            _orig: JSON.stringify({...o, stammOverride: v}),
        };
    });
}

export function editorReducer(state, action) {
    switch (action.type) {
        case 'load-start':
            return {...state, loading: true, loadError: null};

        case 'load-done':
            return {
                ...state,
                loading: false,
                loadError: null,
                mode: action.mode || state.mode,
                order: action.doc,
                rows: toEditorRows(action.doc.items, state.savedOverrides),
                meta: {version: action.doc.version},
                pending: emptyPending,
                stale: false,
                stock: emptyStock,
                // Beleg-PDFs und Schneider-Status hängen an den Lieferscheinen
                // und werden nach dem Laden neu gesucht; Schritt, Marker und
                // Prüf-Haken bleiben stehen (anders als im Original, das beim
                // Neuladen auf Schritt 1 zurücksprang).
                wizard: {
                    ...state.wizard,
                    shipDocs: {id: null, docs: null, loading: false},
                    snvInfo: {id: null, data: null, loading: false},
                },
            };

        case 'load-failed':
            return {...state, loading: false, loadError: action.message};

        case 'stock-start':
            return {...state, stockLoading: true};

        case 'stock-done':
            return {
                ...state,
                stockLoading: false,
                stock: {
                    ...emptyStock,
                    ...action.stock,
                    loaded: true,
                    lahrStockTrusted: action.stock.lahrStockTrusted !== false,
                },
            };

        case 'stock-failed':
            return {...state, stockLoading: false};

        case 'set-view':
            return {...state, view: {...state.view, ...action.patch}};

        case 'reset-view':
            return {...state, view: emptyView};

        case 'toggle-avail': {
            const set = new Set(state.view.availFilter);
            if (set.has(action.cat)) {
                set.delete(action.cat);
            } else {
                set.add(action.cat);
            }
            return {...state, view: {...state.view, availFilter: [...set]}};
        }

        case 'set-sort': {
            // Klick auf eine Spalte schaltet aufsteigend → absteigend → aus.
            const cur = state.view.sort;
            let next;
            if (cur.key === action.key) {
                next = cur.dir === 1 ? {key: action.key, dir: -1} : {key: null, dir: 1};
            } else {
                next = {key: action.key, dir: 1};
            }
            return {...state, view: {...state.view, sort: next}};
        }

        case 'vat-result':
            return {...state, vatResults: {...state.vatResults, [action.vat]: action.result}};

        case 'meta-version':
            return {...state, meta: {...state.meta, version: action.version}};

        // ── Bearbeiten in der Tabelle (Original `renderRows`, 4935–5067) ────

        case 'set-field': {
            let wert = action.value;
            /*
             | Am Lieferschein lässt weclapp Mengen nur senken: mehr als die
             | Auftragsmenge nimmt es nicht an (ABGLEICH R5). Die Obergrenze
             | greift deshalb auch bei der Eingabe von Hand, nicht nur an den
             | Knöpfen −/+ — sonst liefe der Bearbeiter in einen Save, den
             | weclapp still verwirft.
             */
            if (action.field === 'quantity' && state.mode === 'shipment') {
                const r = state.rows[action.index];
                const max = qtyNum((origOf(r) || {}).quantity);
                if (max > 0 && qtyNum(wert) > max) {
                    wert = String(max);
                }
            }
            return {...state, rows: patchRow(state.rows, action.index, {[action.field]: wert})};
        }

        /**
         * −/+ an der Menge. Untergrenze 0; am Lieferschein zusätzlich die
         * Auftragsmenge als Obergrenze (dort lassen sich Mengen nur senken).
         */
        case 'step-qty': {
            const r = state.rows[action.index];
            if (!r) {
                return state;
            }
            let q = Math.max(0, qtyNum(r.quantity) + action.step);
            if (state.mode === 'shipment') {
                const max = qtyNum((origOf(r) || {}).quantity);
                if (max > 0) {
                    q = Math.min(q, max);
                }
            }
            return {...state, rows: patchRow(state.rows, action.index, {quantity: String(q)})};
        }

        /** Lagerwahl einer Position (Klick auf ein Segment des Schiebereglers). */
        case 'set-stamm':
            return {...state, rows: patchRow(state.rows, action.index, {_stammOverride: action.value || null})};

        /**
         * Master-Schalter „alle Lahr" / „alle Schneider": setzt die Wahl für
         * alle Positionen und merkt zugleich das passende Auftragslager vor —
         * weclapp lässt Entnahmen nur am Auftragslager zu (ABGLEICH H9/H10).
         */
        case 'set-all-stamm': {
            // Positionen auf „Keine" bleiben stehen (ABGLEICH H36) — sonst
            // machte ein Klick auf den Master-Schalter eine ausdrückliche
            // Entscheidung lautlos rückgängig.
            const rows = state.rows.map((r) => (
                !r.del && r.articleId && !isExcluded(r) ? {...r, _stammOverride: action.target} : r
            ));
            return {
                ...state,
                rows,
                pending: action.warehouseId
                    ? {...state.pending, warehouse: action.warehouseId}
                    : state.pending,
                view: {...state.view, stammFilter: 'all'},
            };
        }

        /**
         * „alle Lahr/Schneider" bei aktivem Filter (Ticket #124): nur die
         * sichtbaren Zeilen. Auftragslager und Filter bleiben unverändert -
         * wer filtert, will danach weiter dieselben Zeilen sehen.
         */
        case 'set-stamm-many': {
            const set = new Set(action.indices || []);
            return {
                ...state,
                rows: state.rows.map((r, i) => (
                    set.has(i) && !r.del && r.articleId && !isExcluded(r) ? {...r, _stammOverride: action.target} : r
                )),
            };
        }

        /**
         * Mehrfachauswahl (Ticket #124): ein Feld für viele Zeilen setzen.
         * `field` ist 'stamm', 'discountPercentage', 'quantity' oder 'del'.
         * Gelöschte Zeilen ändern nur ihre Löschmarke; „Keine" ist beim
         * Lager eine normale Wahl wie am Schieberegler.
         */
        case 'bulk-patch': {
            const set = new Set(action.indices || []);
            const patchOf = (r) => {
                switch (action.field) {
                    case 'stamm':
                        return {_stammOverride: action.value || null};
                    case 'discountPercentage':
                        return {discountPercentage: String(action.value)};
                    case 'quantity':
                        return {quantity: String(action.value)};
                    case 'del':
                        return {del: !!action.value};
                    default:
                        return null;
                }
            };
            return {
                ...state,
                rows: state.rows.map((r, i) => {
                    if (!set.has(i) || !r.articleId || (r.del && action.field !== 'del')) {
                        return r;
                    }
                    const patch = patchOf(r);
                    return patch ? {...r, ...patch} : r;
                }),
            };
        }

        /** „↺ Stammlager": nur die Abweichler zurücknehmen (ABGLEICH H12). */
        case 'reset-stamm': {
            const rows = state.rows.map((r) => (
                !r.del && r.articleId && r._stammOverride && r._stammOverride !== articleStammOf(r, state.stock)
                    ? {...r, _stammOverride: null}
                    : r
            ));
            return {...state, rows};
        }

        /** Position zum Löschen markieren bzw. die Markierung zurücknehmen. */
        case 'toggle-del': {
            const r = state.rows[action.index];
            if (!r) {
                return state;
            }
            return {...state, rows: patchRow(state.rows, action.index, {del: !r.del})};
        }

        case 'add-row':
            return {
                ...state,
                rows: state.rows.concat([{
                    ...action.row,
                    _key: nextNewRowKey(),
                    _new: true,
                    del: false,
                    _stammOverride: null,
                    _orig: JSON.stringify({quantity: action.row.quantity, discountPercentage: '0', stammOverride: null}),
                }]),
            };

        /** Neu hinzugefügte, noch nicht gespeicherte Zeile wieder entfernen. */
        case 'remove-row': {
            const r = state.rows[action.index];
            if (!r || !r._new) {
                return state;
            }
            const rows = state.rows.slice();
            rows.splice(action.index, 1);
            return {...state, rows};
        }

        /**
         * Einzelne Position ergänzen. `key` statt `index`, wenn die Zeile
         * zwischenzeitlich verschoben sein kann — etwa beim Nachladen der
         * Artikeldaten einer neu hinzugefügten Zeile.
         */
        case 'row-patch': {
            const index = action.key
                ? state.rows.findIndex((r) => r._key === action.key)
                : action.index;
            return {...state, rows: patchRow(state.rows, index, action.patch)};
        }

        /**
         * Nachgeladenes Artikel-Detail einer neuen Zeile in die Bestandsdaten
         * einhängen (Original `loadNewRowDetail`, 5105): Ampel, Stammlager,
         * Rabattgruppe, Masterbox und die Preis-Vorschau.
         */
        case 'article-detail': {
            const id = String(action.articleId);
            const d = action.detail || {};
            const stock = {...state.stock};

            if (d.whStock) {
                stock.whStock = {...stock.whStock, [id]: d.whStock};
                stock.freeByWh = {...stock.freeByWh, [id]: d.free || {}};
                stock.avail = {...stock.avail, [id]: d.avail || {}};
                stock.free = {
                    ...stock.free,
                    [id]: (d.free && d.free[d.orderWarehouse] != null) ? d.free[d.orderWarehouse] : undefined,
                };
            }
            if (d.production) {
                stock.production = {...stock.production, [id]: true};
            }
            if (d.group && stock.groups) {
                stock.groups = {...stock.groups, [id]: d.group};
            }
            if (d.masterbox != null) {
                stock.masterbox = {...stock.masterbox, [id]: d.masterbox};
            }
            if (d.voc != null) {
                stock.voc = {...stock.voc, [id]: d.voc};
            }
            if (d.stamm) {
                stock.stammArt = {...stock.stammArt, [id]: d.stamm};
            }
            if (d.dims) {
                stock.dims = {...stock.dims, [id]: d.dims};
            }
            if (d.name) {
                stock.names = {...stock.names, [id]: d.name};
            }

            return {...state, stock};
        }

        // ── Rabatte anwenden (Original `applyDiscountsDirect` 1337,
        // `applyDiscountGroup` 1350, `applyMasterbox` 1288) ─────────────────
        // Gesetzt wird nur die Arbeitsliste (blau markiert). Der Palettenpreis
        // wirkt über den Vertriebsweg und wird deshalb als Kopf-Änderung
        // vorgemerkt, nicht an den Positionen gesetzt.

        case 'discount-apply-all': {
            const {rows, channel} = applyDiscounts(
                state.rows, state.stock, state.order, state.pending.channel, nextNewRowKey,
            );
            return {
                ...state,
                rows,
                pending: channel ? {...state.pending, channel} : state.pending,
            };
        }

        case 'discount-apply-group': {
            const ergebnis = applyDiscountGroup(
                state.rows, state.stock, state.order, action.key, state.pending.channel, nextNewRowKey,
            );
            return {
                ...state,
                rows: ergebnis.rows,
                pending: ergebnis.channel ? {...state.pending, channel: ergebnis.channel} : state.pending,
            };
        }

        // ── Vorgemerkte Kopf-Änderungen (Original PENDING_*) ────────────────

        case 'set-pending':
            return {...state, pending: {...state.pending, ...action.patch}};

        case 'reset-pending':
            return {...state, pending: emptyPending};

        // ── Arbeitsstand aus dem Ordermanagement ────────────────────────────

        case 'state-loaded': {
            const overrides = action.itemOverrides || {};
            const checked = (action.checkedItemIds || []).map(String);
            return {
                ...state,
                savedOverrides: overrides,
                rows: applyOverrides(state.rows, overrides),
                wizard: {
                    ...state.wizard,
                    checked,
                    savedChecked: checked,
                    kommWiz: {...emptyKommWiz, ...(action.wizard || {})},
                    shipEstimate: action.shipEstimate || null,
                },
            };
        }

        case 'state-saved':
            return {...state, savedOverrides: action.itemOverrides || {}};

        /** Prüf-Haken und Wizard-Marker sind gesichert (Antwort von PUT /state). */
        case 'state-saved-wizard':
            return {
                ...state,
                wizard: {
                    ...state.wizard,
                    savedChecked: (action.checkedItemIds || []).map(String),
                    kommWiz: {...emptyKommWiz, ...(action.wizard || {})},
                    shipEstimate: action.shipEstimate !== undefined
                        ? action.shipEstimate
                        : state.wizard.shipEstimate,
                },
            };

        // ── Speichern ───────────────────────────────────────────────────────

        case 'save-start':
            return {
                ...state,
                save: {
                    ...emptySave,
                    running: true,
                    jobId: action.jobId || null,
                    phase: 'Speichern gestartet…',
                    status: {tone: 'work', text: 'Speichern gestartet…'},
                },
            };

        case 'save-progress': {
            const p = action.progress || {};
            const rest = Math.max(0, Math.round(((p.estimateMs || 0) - (p.elapsedMs || 0)) / 1000));
            return {
                ...state,
                save: {
                    ...state.save,
                    jobId: action.jobId != null ? action.jobId : state.save.jobId,
                    phase: p.phase || state.save.phase,
                    pct: p.pct || 0,
                    restSec: p.done ? null : rest,
                },
            };
        }

        case 'save-status':
            return {...state, save: {...state.save, running: false, status: action.status}};

        case 'save-clear-status':
            return {...state, save: {...state.save, status: null}};

        /**
         * Ergebnis übernehmen (Original `doSave`, 5216–5316): Beleg-Kopf,
         * Summen, Version und die frischen Positionen. Eingaben, die WÄHREND
         * des Speicherns entstanden sind, werden auf den neuen Stand gehoben.
         */
        case 'save-applied': {
            const j = action.result || {};
            const later = action.later || [];
            const order = {
                ...state.order,
                items: j.items,
                netAmount: j.netAmount != null ? j.netAmount : state.order.netAmount,
                version: j.version,
            };

            if (j.salesChannel) {
                order.salesChannel = j.salesChannel;
                order.channelLabel = j.channelLabel;
            }
            if (order.payment) {
                const payment = {...order.payment};
                if (j.salesOrderPaymentType) {
                    payment.current = j.salesOrderPaymentType;
                }
                if (j.advancePaymentAmount != null) {
                    payment.advanceCurrent = j.advancePaymentAmount;
                }
                if (j.grossAmount != null) {
                    payment.gross = j.grossAmount;
                }
                // Kundeninfo nur bei Erfolg übernehmen; sonst bleibt die
                // Eingabe des Bearbeiters stehen und geht nicht verloren.
                if (action.customerDescription != null) {
                    payment.customerDescription = action.customerDescription;
                }
                order.payment = payment;
            }
            if (j.shippingCosts) {
                order.shippingCosts = j.shippingCosts;
            }
            if (j.customerOrderNumber != null) {
                order.customerOrderNumber = j.customerOrderNumber;
            }
            if (j.warehouseId) {
                order.warehouseId = j.warehouseId;
                order.warehouseName = j.warehouseName;
            }

            const overrides = j.itemOverrides && typeof j.itemOverrides === 'object'
                ? j.itemOverrides
                : state.savedOverrides;
            const rows = toEditorRows(j.items, overrides);

            for (const l of later) {
                if (l._new) {
                    rows.push(l);
                    continue;
                }
                const t = rows.find((x) => String(x.id) === String(l.id));
                if (t) {
                    t.quantity = l.quantity;
                    t.discountPercentage = l.discountPercentage;
                    t.del = l.del;
                }
            }

            return {
                ...state,
                order,
                rows,
                savedOverrides: overrides,
                meta: {version: j.version},
                pending: {...emptyPending, custDesc: action.keepCustDesc != null ? action.keepCustDesc : null},
                stale: false,
            };
        }

        // ── Frische-Poll (Original `checkStale`, 2036) ───────────────────────

        case 'stale-show':
            return {...state, stale: true};

        case 'stale-hide':
            return {...state, stale: false};

        /** Stiller Merge: Beleg und Positionen vollständig ersetzen. */
        case 'quiet-merge':
            return {
                ...state,
                order: action.doc,
                rows: toEditorRows(action.doc.items, state.savedOverrides),
                meta: {version: action.doc.version},
                stale: false,
            };

        /**
         * Nur Kopfdaten und gelieferte Mengen nachziehen, ohne die offenen
         * Eingaben zu verlieren (Original `refreshDocMeta`, 1974).
         */
        case 'refresh-meta': {
            const j = action.doc || {};
            const order = {
                ...state.order,
                status: j.status,
                version: j.version,
                editable: j.editable,
                netAmount: j.netAmount,
                grossAmount: j.grossAmount,
                warehouseId: j.warehouseId,
                warehouseName: j.warehouseName,
                shipments: j.shipments,
                openShipments: j.openShipments,
                order: j.order,
            };
            const byId = {};
            for (const it of (j.items || [])) {
                byId[String(it.id)] = it;
            }
            const rows = state.rows.map((row) => {
                const it = byId[String(row.id)];
                // Die Reservierung (Entnahmen) kann sich im Hintergrund geändert
                // haben (automatisches Reservieren) — Eingaben bleiben unberührt.
                return it
                    ? {...row, shippedQuantity: it.shippedQuantity || '0', pickedQty: it.pickedQty ?? row.pickedQty}
                    : row;
            });

            return {
                ...state,
                order,
                rows,
                meta: {version: j.version},
                newDocLinks: action.highlight || [],
            };
        }

        case 'clear-doclinks':
            return {...state, newDocLinks: []};

        // ── Geführter Ablauf (Stufe 3) ──────────────────────────────────────

        /** Schritt wechseln (Original `setWizStep`, 3289: klemmt auf 1…9). */
        case 'wiz-step':
            return {
                ...state,
                wizard: {...state.wizard, step: Math.max(1, Math.min(WIZ_LAST, action.step))},
            };

        /**
         * Teilzustand des Ablaufs setzen (Lazy-Loads, Beleg-PDFs,
         * Schneider-Status, „angekommen", Versandart, Beistell-Wahl).
         */
        case 'wiz-patch':
            return {...state, wizard: {...state.wizard, ...action.patch}};

        /** Marker des Ablaufs (schneiderDone, arrived, komm, verified). */
        case 'wiz-komm':
            return {
                ...state,
                wizard: {...state.wizard, kommWiz: {...state.wizard.kommWiz, ...action.patch}},
            };

        /** Einzelnen Prüf-Haken umschalten (Original `toggleStockCheck`, 3613). */
        case 'wiz-toggle-check': {
            const key = action.key;
            const vorhanden = state.wizard.checked.includes(key);
            const checked = vorhanden
                ? state.wizard.checked.filter((k) => k !== key)
                : state.wizard.checked.concat([key]);
            return {...state, wizard: {...state.wizard, checked}};
        }

        /** Sammel-Haken: Liste vollständig ersetzen (Massenaktion im Spaltenkopf). */
        case 'wiz-set-checked':
            return {...state, wizard: {...state.wizard, checked: (action.checked || []).map(String)}};

        /**
         * Frischer Bestand nach einer Buchung (Original: nach jedem
         * `stock-book`/`pick` wird `/api/stock` neu geholt). Nur die
         * Bestandsfelder werden ersetzt — Rabattregeln und Stammdaten bleiben.
         */
        case 'stock-refresh': {
            const s = action.stock || {};
            return {
                ...state,
                stock: {
                    ...state.stock,
                    free: s.free || {},
                    freeByWh: s.freeByWh || {},
                    avail: s.avail || {},
                    whStock: s.whStock || {},
                },
            };
        }

        /** Reservierte Menge an einer Position fortschreiben. */
        case 'row-picked': {
            const rows = state.rows.map((r) => (
                String(r.id) === String(action.id) ? {...r, pickedQty: action.pickedQty} : r
            ));
            return {...state, rows};
        }

        // ── Lieferlauf (Schritt 3 und 9) ────────────────────────────────────

        case 'deliver-start':
            return {
                ...state,
                deliver: {
                    ...emptyDeliver,
                    running: true,
                    mode: action.mode,
                    status: action.status || null,
                },
            };

        case 'deliver-status':
            return {...state, deliver: {...state.deliver, running: !!action.running, status: action.status}};

        case 'deliver-result':
            return {
                ...state,
                deliver: {...state.deliver, running: false, result: action.result, status: action.status || state.deliver.status},
            };

        case 'deliver-docs':
            return {...state, deliver: {...state.deliver, docs: action.docs}};

        case 'deliver-reset':
            return {...state, deliver: emptyDeliver};

        default:
            return state;
    }
}
